import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import {
    YES,
    IDLE, PENDING,
    VIEW, ADD, EDIT, DELETE,
} from "const"
import { MtTask, MtDriveFile } from "server/models"
import { projectsRoute, tasksRoute } from "client/parts/routes"
import { StateType, hasAccess, hasProjectAccess, replaceHistoryState } from "client/utils"
import {
    PRM_TASK,
    PRM_COMMENTS,
    StoreType,
    addModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    LinkButton,
    Logo,
    DateFormatter,
    CKEditorAttachmentsView,
    Button,
    SpinnerIcon,

    PopupMenu,
    PopupMenuItem,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
} from "client/components"

import TaskEdit from "client/pages/task/edit"
import EditComment from "./edit"
import {
    CommentWrapper,
    CommentDescriptionWrapper,
    CommentTitleRow,
    CommentAuthor,
    CommentActionWrapper,
    CommentAction,
    EditCommentWrapper,
    NewCommentButton,
} from "./styled"


export interface Comment {
    readonly id: number
    readonly comment: string
    readonly author: number
    readonly attachments?: readonly CommentAttachment[]
    readonly createdAt?: Date
    readonly updatedAt?: Date
}
export interface CommentAttachment {
    readonly commentId: number
    readonly fileId: number
    readonly file?: MtDriveFile
    readonly order: number
}

export type CommentValidation = { [key in keyof Comment]?: string}

export type EditCommentType = (activeComment: Comment, editCallback: (comment: Comment) => void) => void
export type SaveCommentType = (
    comment: Comment,
    attachments: readonly MtDriveFile[],
    saveCallback: (commentValidation: CommentValidation) => void
) => void
export type DeleteCommentType = (activeComment: Comment, deleteCallback: () => void) => void


const initComment: Comment = {
    id: null,
    comment: "",
    author: null,
    attachments: [],
}


interface CommentsProps {
    comments: readonly Comment[]
    task?: MtTask
    onEdit: EditCommentType
    onSave: SaveCommentType
    onDelete: DeleteCommentType
}

export const Comments = ({
    comments,
    task = null,
    onEdit,
    onSave,
    onDelete,
}: CommentsProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    // hiddenNumber = null is marker on data load
    const [hiddenNumber, setHiddenNumber] = useState<number>(null)
    const [displayComments, setDisplayComments] = useState<Comment[]>([])
    const [activeComment, setActiveComment] = useState<Comment>()
    const [editableComment, setEditableComment] = useState<Comment>(null)

    const [showPopup, setShowPopup] = useState(false)
    const [popupTarget, setPopupTarget] = useState<HTMLElement>(null)
    const [popupPlacement, setPopupPlacement] = useState<"bottom-start" | "top-start">(null)

    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const [deleteState, setDeleteState] = useState<StateType>(IDLE)

    const hideMenu = () => {
        setShowPopup(false)
    }

    useEffect(() => {
        document.addEventListener("click", hideMenu)
        return () => {
            document.removeEventListener("click", hideMenu)
        }
    }, [])

    useEffect(() => {
        // set hiddenNumber only on comments load
        if (hiddenNumber !== null) return
        if (!comments) return
        setHiddenNumber(comments.length > 3 ? comments.length - 3 : 0) // show last 3 comments
    }, [comments, hiddenNumber])

    useEffect(() => {
        if (hiddenNumber === null) return
        if (!comments) return
        setDisplayComments(comments.slice(hiddenNumber))
    }, [comments, hiddenNumber])

    const onEditComment = () => {
        onEdit(activeComment, (comment) => {
            setEditableComment(comment)
        })
    }

    const onCancelEditComment = () => {
        setEditableComment(null)
    }

    const onCancelDelete = () => {
        setShowConfirmDelete(false)
    }

    const onConfirmDelete = () => {
        setDeleteState(PENDING)
        onDelete(activeComment, () => {
            setDeleteState(IDLE)
            setShowConfirmDelete(false)
            setActiveComment(null)
        })
    }

    // comment access
    const editCommentAccess = !project.id
        ? hasAccess(PRM_COMMENTS, EDIT)
        : hasProjectAccess(PRM_COMMENTS, EDIT)

    const deleteCommentAccess = !project.id
        ? hasAccess(PRM_COMMENTS, DELETE)
        : hasProjectAccess(PRM_COMMENTS, DELETE)

    const isOwnerComment = activeComment && activeComment.author === userData.userId

    // task access
    const addTaskAccess = !project.id
        ? hasAccess(PRM_TASK, ADD)
        : hasProjectAccess(PRM_TASK, ADD)

    const { subTaskModuleAccess } = packageSettings

    const taskBaseRoute = project.id ? `${projectsRoute}/${VIEW}/${project.id}${tasksRoute}` : tasksRoute
    const addTaskUrl = `${taskBaseRoute}/${EDIT}/0`

    const activeCommentAttachments: MtDriveFile[] = []
    if (activeComment) {
        activeComment.attachments.forEach((row) => {
            activeCommentAttachments.push(row.file)
        })
    }

    return (
        <PageForm style={{ paddingBottom: "35px" }}>
            <FormGroup>
                <Label>{lang.comments}</Label>
            </FormGroup>

            {
                hiddenNumber === 0
                && comments && comments.length > 3
                && (
                    <p>
                        <LinkButton onClick={() => setHiddenNumber(comments.length - 3)}>
                            {lang.hideComments}
                        </LinkButton>
                    </p>
                )
            }
            {
                hiddenNumber > 0 && (
                    <p>
                        <LinkButton onClick={() => setHiddenNumber(hiddenNumber > 3 ? hiddenNumber - 3 : 0)}>
                            {`${lang.previousComments} (${hiddenNumber})`}
                        </LinkButton>
                    </p>
                )
            }

            {
                displayComments.map((comment) => {
                    const author = userList[comment.author]
                    const commentNode = (
                        <CommentWrapper key={comment.id}>
                            <Logo
                                size={40}
                                borderRadius={20}
                                logo={author.logo || Images.noAvatar}
                            />
                            <CommentDescriptionWrapper>
                                <CommentTitleRow>
                                    <CommentAuthor>{`${author.firstName} ${author.lastName}`}</CommentAuthor>
                                    <DateFormatter date={comment.createdAt} />
                                </CommentTitleRow>
                                <CKEditorAttachmentsView
                                    description={comment.comment}
                                    attachments={comment.attachments}
                                    commentMode
                                />

                                <CommentActionWrapper>
                                    <CommentAction onClick={(e) => {
                                        e.stopPropagation()
                                        setShowPopup(true)
                                        setPopupTarget(e.currentTarget as HTMLElement)

                                        if (window.innerHeight - e.clientY < 180) setPopupPlacement("top-start")
                                        else setPopupPlacement("bottom-start")

                                        setActiveComment(comment)
                                    }}
                                    >
                                        {lang.more_no_dots}
                                    </CommentAction>
                                </CommentActionWrapper>
                            </CommentDescriptionWrapper>
                        </CommentWrapper>
                    )

                    if (editableComment && editableComment.id === comment.id) { // edit existing comment
                        return (
                            <div key={`wrapper_${comment.id}`}>
                                {commentNode}
                                <EditComment
                                    comment={editableComment}
                                    onSave={onSave}
                                    onCancel={onCancelEditComment}
                                />
                            </div>
                        )
                    }
                    return commentNode
                })
            }

            {
                editableComment && editableComment.id === null // create new comment
                && (
                    <EditComment
                        comment={editableComment}
                        onSave={onSave}
                        onCancel={onCancelEditComment}
                    />
                )
            }

            {
                !editableComment && ( // new comment button
                    <EditCommentWrapper>
                        <Logo
                            size={40}
                            borderRadius={20}
                            logo={userData.userLogo || Images.noAvatar}
                        />
                        <NewCommentButton onClick={() => setEditableComment(initComment)}>
                            {lang.addComment}
                        </NewCommentButton>
                    </EditCommentWrapper>
                )
            }

            <PopupMenu
                show={showPopup}
                target={popupTarget}
                placement={popupPlacement}
            >
                {
                    (editCommentAccess || isOwnerComment) && (
                        <PopupMenuItem onClick={onEditComment}>
                            {lang.edit}
                        </PopupMenuItem>
                    )
                }
                {
                    (deleteCommentAccess || isOwnerComment) && (
                        <PopupMenuItem onClick={() => setShowConfirmDelete(true)}>
                            {lang.delete}
                        </PopupMenuItem>
                    )
                }
                {
                    addTaskAccess && activeComment && (
                        <PopupMenuItem onClick={() => {
                            const prevUrl = window.location.pathname
                            replaceHistoryState(addTaskUrl)
                            dispatch(addModal({
                                key: "addTask",
                                content: <TaskEdit
                                    taskId={0}
                                    description={activeComment.comment}
                                    attachments={activeCommentAttachments}
                                />,
                                onClose: () => replaceHistoryState(prevUrl),
                            }))
                        }}
                        >
                            {lang.createTask}
                        </PopupMenuItem>
                    )
                }
                {
                    task // subtasks can be added only on task level
                    && addTaskAccess && subTaskModuleAccess === YES && activeComment
                    && (
                        <PopupMenuItem onClick={() => {
                            const prevUrl = window.location.pathname
                            replaceHistoryState(addTaskUrl)
                            dispatch(addModal({
                                key: "addSubtask",
                                content: <TaskEdit
                                    taskId={0}
                                    parentTaskId={task.id}
                                    description={activeComment.comment}
                                    attachments={activeCommentAttachments}
                                />,
                                onClose: () => replaceHistoryState(prevUrl),
                            }))
                        }}
                        >
                            {lang.createSubtask}
                        </PopupMenuItem>
                    )
                }
            </PopupMenu>

            <ConfirmWrapper show={showConfirmDelete} onClose={onCancelDelete}>
                <Confirm>
                    <ConfirmTitle>{lang.deleteCommentConfirm}</ConfirmTitle>
                    <ConfirmBottom>
                        <Button variant="success" row onClick={onConfirmDelete}>
                            {deleteState === PENDING ? <SpinnerIcon /> : lang.confirm}
                        </Button>
                        <Button row onClick={onCancelDelete}>{lang.cancel}</Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </PageForm>
    )
}
